import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useSearchParams } from 'react-router-dom';
import { useFileDownload } from '../Hooks/useFileDownload';
import { useBuslogPrepare } from '../Hooks/useBuslogPrepare';
import { useBuslogSignals } from '../Hooks/useBuslogSignals';
import { usePlayback } from '../Hooks/usePlayback';
import { useUserPreferences } from '../Hooks/useUserPreferences';
import { useNotification } from '../common/NotificationContext';
import { getPlaybackTimestamp } from './visualizeData.constants';
import BuslogPanels from './BuslogPanels';
import SignalSearch from './SignalSearch';
import './VisualizeData.styles.css';

const VisualizeDataPresenter = ({ file, blobUrl, onError }) => {
  const { downloadByFilenames } = useFileDownload([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchParams] = useSearchParams();
  const currentSignalsParam = searchParams.get('signals') || '';

  const filePath = useMemo(() => {
    if (typeof file === 'string') return file;
    if (file?.filename) return file.filename;
    if (file?.name) return file.name;
    if (file?.file_path) return file.file_path;
    return '';
  }, [file]);

  // --- Buslog preparation ---
  const prep = useBuslogPrepare(filePath);

  // --- Signal visualisation ---
  const signals = useBuslogSignals(filePath);  // --- Download ---
  const handleDownloadFile = useCallback(async () => {
    if (!filePath) return;
    setIsDownloading(true);
    try {
      await downloadByFilenames([filePath]);
    } catch {
      // handled silently
    } finally {
      setIsDownloading(false);
    }
  }, [filePath, downloadByFilenames]);

  // --- Playback State ---
  const totalDurationSec = useMemo(() => {
    if (!prep.end_ts || !prep.start_ts) return 0;
    return (prep.end_ts - prep.start_ts) / 1e9;
  }, [prep.end_ts, prep.start_ts]);
  
  const playback = usePlayback(totalDurationSec);

  const playbackTimestamp = useMemo(() => {
    if (!prep.start_ts) return '';
    return getPlaybackTimestamp(prep.start_ts, playback.currentTimeSec);
  }, [prep.start_ts, playback.currentTimeSec]);

  // --- Saved Signals (Bookmark) ---
  const [isSaved, setIsSaved] = useState(false);
  const { savePreferencesToApi } = useUserPreferences();
  const notify = useNotification();

  useEffect(() => {
    try {
      const saved = localStorage.getItem('visualize_signals_preferences');
      setIsSaved(saved === currentSignalsParam && currentSignalsParam !== '');
    } catch {
      setIsSaved(false);
    }
  }, [currentSignalsParam]);

  const handleSaveSearchClick = useCallback(async () => {
    try {
      if (isSaved) {
        localStorage.removeItem('visualize_signals_preferences');
        await savePreferencesToApi({ visualize_signals_preferences: null });
        setIsSaved(false);
        notify({ message: 'Saved signals unsaved.', severity: 'info' });
      } else {
        if (currentSignalsParam) {
          localStorage.setItem('visualize_signals_preferences', currentSignalsParam);
          await savePreferencesToApi({ visualize_signals_preferences: currentSignalsParam });
          setIsSaved(true);
          notify({ message: 'Signals saved successfully.', severity: 'success' });
        }
      }
    } catch (e) {
      console.error('Failed to save signal preferences', e);
      notify({ message: 'Failed to save signal preferences.', severity: 'error' });
    }
  }, [isSaved, currentSignalsParam, savePreferencesToApi, notify]);

  // --- No file ---
  if (!filePath) {
    return (
      <Box className="visualize-main-container">
        <Box className="error-container">
          <Alert severity="error" className="visualize-error-alert">
            No file path provided. Please ensure you are accessing this page with a valid file.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Expected URL format: /preview?filename=path/to/file.zip&category=buslog
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      {/* Header Banner - Floating */}
      <Box className="visualize-floating-header">

        <Box className="visualize-header-left">
          {prep.isFileReady && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'text.secondary' }}>
                {filePath.split('/').pop() || filePath}
              </Typography>
            </Box>
          )}
        </Box>

        <Box className="visualize-header-right">
          <Tooltip title="Download File">
            <span>
              <IconButton
                onClick={handleDownloadFile}
                disabled={isDownloading || !filePath}
                className="visualize-download-button"
                sx={{ color: 'primary.main' }}
              >
                {isDownloading ? <CircularProgress size={24} /> : <DownloadIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box className="visualize-main-container-wrapper">
        {/* Main Content */}
        <BuslogPanels
          prep={prep}
          signals={signals}
          playback={playback}
          playbackTimestamp={playbackTimestamp}
          filePath={filePath}
          isSaved={isSaved}
          handleSaveSearchClick={handleSaveSearchClick}
          currentSignalsParam={currentSignalsParam}
        />
      </Box>
    </Box>
  );
};

export default VisualizeDataPresenter;
